import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import SkuflixBroadcastIcon from "core/icons/skuflix/SkuflixBroadcastIcon";
import { playerFooterControlPadding } from "core/utils/playerFooterControlPadding";

const StyledLiveButton = styled(Button)(({ theme }) => ({
  minWidth: "auto",
  ...playerFooterControlPadding(theme),
}));

function LiveButton({ videoState, videoActions, liveSeekBar }) {
  const { t } = useTranslation("core");
  const [isNotLiveByPause, setIsNotLiveByPause] = useState(false);

  if (!videoState) return null;
  if (!liveSeekBar) return null;
  if (!videoState.live) return null;

  const onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isNotLiveByPause) {
      setIsNotLiveByPause(false);

      videoActions.seekToLive();
    } else {
      liveSeekBar.setOffset(0);
    }
  };

  useEffect(() => {
    if (!videoState.isUserPause) {
      return;
    }

    if (liveSeekBar.offset !== 0) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsNotLiveByPause(true);
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [videoState.isUserPause, liveSeekBar.offset]);

  const isLive = !isNotLiveByPause && liveSeekBar.offset === 0;

  const liveColor = isLive ? "#F44336" : "rgba(255,255,255,0.3)";

  const icon = (
    <SkuflixBroadcastIcon
      sx={{
        color: liveColor,
      }}
    />
  );

  if (isLive) {
    return (
      <StyledLiveButton onClick={onClick} disabled>
        {icon}
      </StyledLiveButton>
    );
  }

  return (
    <Tooltip title={t("goToLive")}>
      <StyledLiveButton onClick={onClick}>{icon}</StyledLiveButton>
    </Tooltip>
  );
}

LiveButton.propTypes = {
  liveSeekBar: PropTypes.shape({
    setOffset: PropTypes.func.isRequired,
    offset: PropTypes.number,
  }),
  videoState: PropTypes.shape({
    live: PropTypes.bool,
    paused: PropTypes.bool,
    isUserPause: PropTypes.bool,
  }),
  videoActions: PropTypes.shape({
    seekToLive: PropTypes.func.isRequired,
  }),
};

export default LiveButton;
